import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { IMAGES } from "@/lib/images";

const slides = [
  { src: IMAGES.kitchen, caption: "Cucina · Kitchen" },
  { src: IMAGES.living, caption: "Soggiorno · Living" },
  { src: IMAGES.terrace, caption: "Terrazzo al tramonto · Terrace at sunset" },
  { src: IMAGES.terraceDay, caption: "Terrazzo · Terrace" },
  { src: IMAGES.terraceChairs, caption: "Terrazzo · Terrace" },
  { src: IMAGES.tableSet, caption: "Sala da pranzo · Dining" },
  { src: IMAGES.tableSet2, caption: "Tavolo · Table" },
  { src: IMAGES.bedroomFull, caption: "Camera da letto · Bedroom" },
  { src: IMAGES.bedroomDoor, caption: "Camera da letto · Bedroom" },
  { src: IMAGES.bedroom1, caption: "Letti a castello · Bunk beds" },
  { src: IMAGES.bathroom, caption: "Bagno · Bathroom" },
  { src: IMAGES.bathroomDetail, caption: "Bagno · Bathroom" },
  { src: IMAGES.amenities, caption: "Servizi · Amenities" },
  { src: IMAGES.cabinetDishes, caption: "Dotazioni cucina · Kitchen equipment" },
  { src: IMAGES.cabinetGlasses, caption: "Bicchieri e tazze · Glasses & mugs" },
  { src: IMAGES.drawerKit, caption: "Cassetto dotazioni · Drawer equipment" },
  { src: IMAGES.drawerCutlery, caption: "Posate · Cutlery" },
  { src: IMAGES.dishwasher, caption: "Lavastoviglie · Dishwasher" },
  { src: IMAGES.appliances, caption: "Elettrodomestici · Appliances" },
  { src: IMAGES.dining, caption: "Vista interna · Interior view" },
];

export default function Gallery() {
  const { t } = useLang();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center" },
    [Autoplay({ delay: 2000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (i) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 sm:py-32 bg-[#F0EDE6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-overline text-[#0097b2] mb-4">
              {t.gallery.overline}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#121C26]">
              {t.gallery.title}
            </h2>
          </div>
          <p className="text-[#4A5D6B] max-w-sm">{t.gallery.subtitle}</p>
        </div>

        <div className="relative" data-testid="gallery-carousel">
          <div className="overflow-hidden bg-[#121C26]" ref={emblaRef}>
            <div className="flex">
              {slides.map((s, i) => (
                <div
                  key={i}
                  className="relative flex-[0_0_100%] min-w-0 aspect-[16/10] sm:aspect-[16/9]"
                  data-testid={`gallery-slide-${i}`}
                >
                  <img
                    src={s.src}
                    alt={s.caption}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#121C26]/70 to-transparent px-6 py-5">
                    <p className="text-xs uppercase tracking-overline text-white/85">
                      {String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                    </p>
                    <p className="font-serif text-xl md:text-2xl text-white mt-1">
                      {s.caption}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            data-testid="gallery-prev"
            onClick={scrollPrev}
            aria-label="Precedente"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FAF9F6]/90 backdrop-blur hover:bg-white text-[#1A3B4D] flex items-center justify-center shadow-lg transition-colors"
          >
            <ChevronLeft size={22} strokeWidth={1.5} />
          </button>
          <button
            data-testid="gallery-next"
            onClick={scrollNext}
            aria-label="Successiva"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FAF9F6]/90 backdrop-blur hover:bg-white text-[#1A3B4D] flex items-center justify-center shadow-lg transition-colors"
          >
            <ChevronRight size={22} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-6" data-testid="gallery-dots">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              data-testid={`gallery-dot-${i}`}
              onClick={() => scrollTo(i)}
              aria-label={`Vai alla foto ${i + 1}`}
              className={`transition-all rounded-full ${
                i === selectedIndex
                  ? "w-8 h-2 bg-[#1A3B4D]"
                  : "w-2 h-2 bg-[#1A3B4D]/30 hover:bg-[#1A3B4D]/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
